import React, { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Header } from '../components/layout/Header'
import { Card } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { FileText, Download, Calendar, Plus, Eye } from 'lucide-react'
import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

interface Report {
  id: string
  report_id?: string
  title: string
  label?: string
  type: 'quarterly' | 'market' | 'custom'
  year?: number
  quarter?: number
  createdAt: string
  status: 'completed' | 'processing' | 'failed' | 'ready' | 'generating'
  downloadUrl?: string
}

interface ReportsResponse {
  reports: Array<{
    id: string
    report_id: string
    year: number
    quarter: number
    label: string
    status: string
    type: string
  }>
  total: number
}

export const ReportsPage: React.FC = () => {
  const [showGenerateForm, setShowGenerateForm] = useState(false)
  const [reportType, setReportType] = useState<'quarterly' | 'market' | 'custom'>('quarterly')
  const [quarter, setQuarter] = useState('Q1-2024')

  const { data: reports, refetch } = useQuery<Report[]>({
    queryKey: ['reports'],
    queryFn: async () => {
      const { data } = await axios.get<ReportsResponse>(`${API_BASE_URL}/reports`)
      // Transform backend response to match frontend interface
      const reportsArray = data.reports || []
      return reportsArray.map(r => ({
        id: r.id || r.report_id,
        report_id: r.report_id,
        title: r.label || `Q${r.quarter} ${r.year} Report`,
        type: (r.type || 'quarterly') as 'quarterly' | 'market' | 'custom',
        year: r.year,
        quarter: r.quarter,
        createdAt: new Date().toISOString(), // Backend doesn't provide this yet
        status: r.status === 'ready' ? 'completed' : r.status === 'generating' ? 'processing' : r.status as any,
        downloadUrl: r.status === 'ready' ? `${API_BASE_URL}/reports/export/pdf/${r.report_id}` : undefined,
      }))
    },
  })

  const generateMutation = useMutation({
    mutationFn: async (params: { year: number; quarter: number; format?: string }) => {
      const { data } = await axios.post(`${API_BASE_URL}/reports/generate`, params)
      return data
    },
    onSuccess: () => {
      refetch()
      setShowGenerateForm(false)
    },
  })

  const handleGenerate = () => {
    // Parse quarter string like "Q1-2024" to year and quarter
    const parts = quarter.split('-')
    const quarterNum = parseInt(parts[0].replace('Q', ''))
    const year = parseInt(parts[1])
    generateMutation.mutate({ year, quarter: quarterNum, format: 'json' })
  }

  const handleDownload = async (report: Report) => {
    if (!report.downloadUrl) return
    const link = document.createElement('a')
    link.href = report.downloadUrl
    link.download = `${report.title}.pdf`
    link.click()
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
      <Header />

      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>Reports</h1>
            <p style={{ color: '#666', fontSize: '16px' }}>
              Generate and manage market expansion reports
            </p>
          </div>
          <Button onClick={() => setShowGenerateForm(true)}>
            <Plus style={{ marginRight: '8px' }} /> Generate Report
          </Button>
        </div>

        {/* Generate Form Modal */}
        {showGenerateForm && (
          <Card style={{ marginBottom: '24px', border: '2px solid #4A90E2' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '16px' }}>Generate New Report</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                  Report Type
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value as any)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  <option value="quarterly">Quarterly Business Review</option>
                  <option value="market">Market Analysis</option>
                  <option value="custom">Custom Report</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                  Quarter
                </label>
                <input
                  type="text"
                  value={quarter}
                  onChange={(e) => setQuarter(e.target.value)}
                  placeholder="Q1-2024"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button variant="outline" onClick={() => setShowGenerateForm(false)}>
                  Cancel
                </Button>
                <Button onClick={handleGenerate} disabled={generateMutation.isPending}>
                  {generateMutation.isPending ? 'Generating...' : 'Generate'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Reports List */}
        <div style={{ display: 'grid', gap: '16px' }}>
          {reports?.map((report) => (
            <Card key={report.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '8px',
                      backgroundColor: '#E8F4FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FileText size={24} style={{ color: '#4A90E2' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>{report.title}</h3>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#666' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={14} />
                        {new Date(report.createdAt).toLocaleDateString()}
                      </span>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          backgroundColor:
                            report.status === 'completed'
                              ? '#E8F5E9'
                              : report.status === 'processing'
                              ? '#FFF3E0'
                              : '#FFEBEE',
                          color:
                            report.status === 'completed'
                              ? '#2E7D32'
                              : report.status === 'processing'
                              ? '#F57C00'
                              : '#C62828',
                        }}
                      >
                        {report.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {report.status === 'completed' && (
                    <>
                      <Button variant="outline" size="sm">
                        <Eye style={{ marginRight: '4px' }} /> Preview
                      </Button>
                      <Button size="sm" onClick={() => handleDownload(report)}>
                        <Download style={{ marginRight: '4px' }} /> Download
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {(!reports || reports.length === 0) && (
          <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
            <FileText size={48} style={{ color: '#CCC', margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '18px', marginBottom: '8px', color: '#666' }}>No reports yet</h3>
            <p style={{ color: '#999', marginBottom: '24px' }}>
              Generate your first report to get started
            </p>
            <Button onClick={() => setShowGenerateForm(true)}>
              <Plus style={{ marginRight: '8px' }} /> Generate Report
            </Button>
          </Card>
        )}
      </main>
    </div>
  )
}
